import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { PencilLine, UserMinus } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React from "react";
import { useTranslation } from "react-i18next";
import { View, XStack, YStack } from "tamagui";
import { useApi } from "../core/apis";
import { getDayJs } from "../core/dayJs";
import { useUploadAvatar } from "../core/hooks/useUploadAvatar";
import { useStores } from "../core/stores";
import { isRunningInApp } from "../core/utils";
import { sendMessageToReactNative } from "../native/webapplogic";
import { MessageType } from "../native/webviewtypes";
import { Body } from "../ui/Body";
import { Box } from "../ui/Box";
import { Button } from "../ui/Button";
import { Group } from "../ui/Group";
import { LoadingCircular } from "../ui/LoadingCircular";
import { Screen } from "../ui/Screen";
import { Title, TitleTextarea } from "../ui/Title";
import { AvatarSize, UserAvatar } from "../ui/UserAvatar";

type Props = NativeStackScreenProps<ReactNavigation.RootParamList, "Profile">;

export const ProfileScreen = observer(({ route }: Props) => {
  const userUid = route.params.userUid;

  const { t } = useTranslation();
  const { sessionStore, usersStore } = useStores();
  const { usersApi } = useApi();
  const isApp = isRunningInApp();
  const user = usersStore.byId.get(userUid);

  const [uploading, setIsUploading] = React.useState(false);

  const uploadFileButton = useUploadAvatar(
    setIsUploading,

    async (url) => {
      await user?.setAvatarUrl(url);
    },
    sessionStore.uid,
  );

  React.useEffect(() => {
    const unsub = usersApi.watchItem(userUid);
    return () => {
      unsub();
    };
  }, [usersApi, userUid]);

  if (!user) {
    return null;
  }

  const allowedToEditProfile = user.data.authedUsers.includes(sessionStore.uid);

  return (
    <Screen title={t("profile")}>
      <View>
        <Box>
          <XStack gap="$large" alignItems="center" flex={1}>
            {!uploading && (
              <UserAvatar userUid={user.uid} size={AvatarSize.Large} />
            )}
            {uploading && <LoadingCircular size={128} />}
            <Group borderTop={false}>
              {allowedToEditProfile ? (
                <TitleTextarea
                  type={"inline"}
                  placeholder="Set your display name"
                  icon={PencilLine}
                  iconFirst
                  blurOnSubmit
                  defaultValue={user.data.displayName ?? ""}
                  onSubmitEditing={(e) =>
                    user.setDisplayName(e.nativeEvent.text)
                  }
                  submitOnEnter
                />
              ) : (
                <Title>{user.data.displayName}</Title>
              )}
              <Body>{getDayJs(user.data.lastActivity)}</Body>
            </Group>
          </XStack>
        </Box>
        {allowedToEditProfile && (
          <YStack gap={"$medium"}>
            {!isApp && uploadFileButton}

            {isApp && (
              <Button
                icon={UserMinus}
                type={"danger"}
                onPress={() => {
                  sendMessageToReactNative({
                    type: MessageType.DeleteAccount,
                  });
                }}
              >
                Delete account
              </Button>
            )}
          </YStack>
        )}
      </View>
    </Screen>
  );
});

export default ProfileScreen;
